/**
 * Core Web Vitals & Performance Monitoring Service
 *
 * Features:
 * - Real-time Web Vitals collection (FCP, LCP, CLS, TTI, INP)
 * - Performance data aggregation and storage
 * - Device and network performance correlation
 * - Route-specific performance analysis
 * - Performance alerts and recommendations
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'
import { logger } from './logger'

// Performance thresholds based on Core Web Vitals
export const PERFORMANCE_THRESHOLDS = {
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint (ms)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
  TTI: { good: 3800, poor: 7300 }, // Time to Interactive (ms)
} as const

interface PerformanceMetric {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  delta?: number
  id: string
  navigationType?: string
}

interface DeviceInfo {
  userAgent: string
  screen: {
    width: number
    height: number
    pixelRatio: number
  }
  connection?: {
    effectiveType: string
    downlink: number
    rtt: number
  }
  memory?: number
  hardwareConcurrency?: number
}

interface RoutePerformance {
  route: string
  metrics: PerformanceMetric[]
  timestamp: number
  deviceInfo: DeviceInfo
  sessionId: string
}

// Global singleton instance tracker
let globalInstanceCount = 0
let globalInitialized = false

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric[]> = new Map()
  private sessionId: string
  private currentRoute: string = '/'
  private deviceInfo: DeviceInfo
  private isEnabled: boolean = true
  // private _isInitialized: boolean = false // Removed as not needed
  private observers: PerformanceObserver[] = []
  private instanceId: number

  constructor() {
    this.instanceId = ++globalInstanceCount

    // Debug logging for multiple instances
    if (globalInstanceCount > 1) {
      console.warn(
        `⚠️ Multiple PerformanceMonitor instances created: ${globalInstanceCount}`
      )
    }

    this.sessionId = this.generateSessionId()
    this.deviceInfo = this.collectDeviceInfo()
    this.initializeMonitoring()
  }

  /**
   * Initialize performance monitoring
   */
  public initializeMonitoring(): void {
    if (!this.isEnabled || typeof window === 'undefined' || globalInitialized) {
      return
    }

    globalInitialized = true
    // Initialization completed
    if (import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true') {
      logger.info(
        `🚀 Performance monitoring initialized (instance ${this.instanceId})`,
        null,
        'performance'
      )
    }

    // Monitor Core Web Vitals
    this.setupCoreWebVitals()

    // Monitor navigation and resource timing
    this.setupNavigationMonitoring()

    // Monitor custom metrics
    this.setupCustomMetrics()

    // Monitor route changes
    this.setupRouteChangeMonitoring()

    // Send initial page load metrics
    this.scheduleMetricsCollection()
  }

  /**
   * Set up Core Web Vitals monitoring
   */
  private setupCoreWebVitals(): void {
    // First Contentful Paint
    onFCP(metric => {
      this.recordMetric('FCP', metric)
    })

    // Largest Contentful Paint
    onLCP(metric => {
      this.recordMetric('LCP', metric)
    })

    // Cumulative Layout Shift
    onCLS(metric => {
      this.recordMetric('CLS', metric)
    })

    // Interaction to Next Paint
    onINP(metric => {
      this.recordMetric('INP', metric)
    })

    // Time to First Byte
    onTTFB(metric => {
      this.recordMetric('TTFB', metric)
    })
  }

  /**
   * Set up navigation and resource timing monitoring
   */
  private setupNavigationMonitoring(): void {
    if (!('PerformanceObserver' in window)) {
      return
    }

    // Navigation timing
    const navObserver = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming
          this.processNavigationTiming(navEntry)
        }
      })
    })

    try {
      navObserver.observe({ entryTypes: ['navigation'] })
      this.observers.push(navObserver)
    } catch (error) {
      console.warn('Navigation timing not supported:', error)
    }

    // Resource timing
    const resourceObserver = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        if (entry.entryType === 'resource') {
          this.processResourceTiming(entry as PerformanceResourceTiming)
        }
      })
    })

    try {
      resourceObserver.observe({ entryTypes: ['resource'] })
      this.observers.push(resourceObserver)
    } catch (error) {
      console.warn('Resource timing not supported:', error)
    }
  }

  /**
   * Set up custom performance metrics
   */
  private setupCustomMetrics(): void {
    // Time to Interactive (custom implementation)
    this.measureTimeToInteractive()

    // Bundle load time
    this.measureBundleLoadTime()

    // React hydration time
    this.measureReactHydrationTime()
  }

  /**
   * Set up route change monitoring
   */
  private setupRouteChangeMonitoring(): void {
    // Monitor hash changes
    window.addEventListener('hashchange', () => {
      this.onRouteChange(window.location.pathname + window.location.hash)
    })

    // Monitor history API changes
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState

    history.pushState = function (...args) {
      originalPushState.apply(history, args)
      performanceMonitor.onRouteChange(window.location.pathname)
    }

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args)
      performanceMonitor.onRouteChange(window.location.pathname)
    }

    // Monitor popstate events
    window.addEventListener('popstate', () => {
      this.onRouteChange(window.location.pathname)
    })
  }

  /**
   * Record a performance metric
   */
  private recordMetric(
    name: string,
    metric: {
      value: number
      rating?: string
      delta?: number
      id?: string
      name?: string
    }
  ): void {
    const performanceMetric: PerformanceMetric = {
      name,
      value: metric.value,
      rating:
        (metric.rating as 'good' | 'needs-improvement' | 'poor') ||
        this.calculateRating(name, metric.value),
      delta: metric.delta,
      id: metric.id || this.generateMetricId(),
      navigationType: this.getNavigationType(),
    }

    const routeMetrics = this.metrics.get(this.currentRoute) || []
    routeMetrics.push(performanceMetric)
    this.metrics.set(this.currentRoute, routeMetrics)

    // Log metric for debugging
    if (process.env.NODE_ENV === 'development') {
      if (import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true') {
        logger.debug(
          `📊 ${name}: ${metric.value.toFixed(2)} (${performanceMetric.rating})`,
          null,
          'performance'
        )
      }
    }

    // Send to analytics if in production
    if (process.env.NODE_ENV === 'production') {
      this.sendMetricToAnalytics(performanceMetric)
    }

    // Check for performance alerts
    this.checkPerformanceAlerts(performanceMetric)
  }

  /**
   * Process navigation timing entry
   */
  private processNavigationTiming(entry: PerformanceNavigationTiming): void {
    const metrics = {
      DNS: entry.domainLookupEnd - entry.domainLookupStart,
      TCP: entry.connectEnd - entry.connectStart,
      Request: entry.responseStart - entry.requestStart,
      Response: entry.responseEnd - entry.responseStart,
      Processing: entry.domComplete - entry.responseEnd,
      Load: entry.loadEventEnd - entry.loadEventStart,
    }

    Object.entries(metrics).forEach(([name, value]) => {
      if (value > 0) {
        this.recordMetric(`Navigation_${name}`, {
          value,
          id: this.generateMetricId(),
        })
      }
    })
  }

  /**
   * Process resource timing entry
   */
  private processResourceTiming(entry: PerformanceResourceTiming): void {
    // Focus on critical resources
    const isCritical =
      entry.name.includes('.js') ||
      entry.name.includes('.css') ||
      entry.name.includes('fonts')

    if (isCritical && entry.duration > 100) {
      this.recordMetric('Resource_Load', {
        value: entry.duration,
        id: this.generateMetricId(),
        name: entry.name,
      })
    }
  }

  /**
   * Measure Time to Interactive
   */
  private measureTimeToInteractive(): void {
    // Simple TTI implementation - when no long tasks for 5 seconds
    let lastLongTask = 0

    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          if (entry.duration > 50) {
            lastLongTask = entry.startTime + entry.duration
          }
        })
      })

      try {
        observer.observe({ entryTypes: ['longtask'] })
        this.observers.push(observer)

        // Check TTI after 5 seconds of no long tasks
        setTimeout(() => {
          const tti = lastLongTask || performance.now()
          this.recordMetric('TTI', { value: tti, id: this.generateMetricId() })
        }, 5000)
      } catch (error) {
        console.warn('Long task monitoring not supported:', error)
      }
    }
  }

  /**
   * Measure bundle load time
   */
  private measureBundleLoadTime(): void {
    const bundleEntries = performance
      .getEntriesByType('resource')
      .filter(
        entry => entry.name.includes('/assets/') && entry.name.includes('.js')
      )

    if (bundleEntries.length > 0) {
      const totalBundleTime = bundleEntries.reduce(
        (sum, entry) => sum + entry.duration,
        0
      )
      this.recordMetric('Bundle_Load', {
        value: totalBundleTime,
        id: this.generateMetricId(),
      })
    }
  }

  /**
   * Measure React hydration time
   */
  private measureReactHydrationTime(): void {
    // Mark the start of React hydration
    performance.mark('react-hydration-start')

    // Use requestIdleCallback to detect when React is likely done
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        performance.mark('react-hydration-end')
        performance.measure(
          'react-hydration',
          'react-hydration-start',
          'react-hydration-end'
        )

        const measure = performance.getEntriesByName('react-hydration')[0]
        if (measure) {
          this.recordMetric('React_Hydration', {
            value: measure.duration,
            id: this.generateMetricId(),
          })
        }
      })
    }
  }

  /**
   * Handle route changes
   */
  private onRouteChange(newRoute: string): void {
    // Save metrics for previous route
    if (this.currentRoute !== newRoute) {
      this.saveRouteMetrics()
      this.currentRoute = newRoute

      // Mark route change for new metrics
      performance.mark(`route-change-${newRoute}`)
    }
  }

  /**
   * Schedule periodic metrics collection
   */
  private scheduleMetricsCollection(): void {
    // Collect metrics every 30 seconds
    setInterval(() => {
      this.collectCurrentMetrics()
    }, 30000)

    // Collect metrics before page unload
    window.addEventListener('beforeunload', () => {
      this.saveRouteMetrics()
    })

    // Collect metrics when page becomes hidden
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.saveRouteMetrics()
      }
    })
  }

  /**
   * Collect current performance metrics
   */
  private collectCurrentMetrics(): void {
    // Get current Core Web Vitals - using the same onX methods for current collection
    onCLS(metric => this.recordMetric('CLS_Current', metric), {
      reportAllChanges: true,
    })
    onFCP(metric => this.recordMetric('FCP_Current', metric))
    onINP(metric => this.recordMetric('INP_Current', metric))
    onLCP(metric => this.recordMetric('LCP_Current', metric), {
      reportAllChanges: true,
    })
    onTTFB(metric => this.recordMetric('TTFB_Current', metric))
  }

  /**
   * Save route-specific metrics
   */
  private saveRouteMetrics(): void {
    const routeMetrics = this.metrics.get(this.currentRoute)
    if (!routeMetrics || routeMetrics.length === 0) {
      return
    }

    const routePerformance: RoutePerformance = {
      route: this.currentRoute,
      metrics: routeMetrics,
      timestamp: Date.now(),
      deviceInfo: this.deviceInfo,
      sessionId: this.sessionId,
    }

    // Save to localStorage for local analysis
    this.saveToLocalStorage(routePerformance)

    // Send to analytics in production
    if (process.env.NODE_ENV === 'production') {
      this.sendRouteMetricsToAnalytics(routePerformance)
    }

    // Clear metrics for this route
    this.metrics.set(this.currentRoute, [])
  }

  /**
   * Save metrics to localStorage
   */
  private saveToLocalStorage(routePerformance: RoutePerformance): void {
    try {
      const existingData = localStorage.getItem('mntnd_performance_metrics')
      const data = existingData ? JSON.parse(existingData) : []

      data.push(routePerformance)

      // Keep only last 100 entries to prevent storage bloat
      if (data.length > 100) {
        data.splice(0, data.length - 100)
      }

      localStorage.setItem('mntnd_performance_metrics', JSON.stringify(data))
    } catch (error) {
      console.warn('Could not save performance metrics to localStorage:', error)
    }
  }

  /**
   * Send metric to analytics service
   */
  private sendMetricToAnalytics(metric: PerformanceMetric): void {
    // Implement analytics integration here
    // Example: Google Analytics, Firebase Analytics, or custom endpoint
    if (import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true') {
      logger.debug('📈 Sending metric to analytics:', metric, 'performance')
    }
  }

  /**
   * Send route metrics to analytics service
   */
  private sendRouteMetricsToAnalytics(
    routePerformance: RoutePerformance
  ): void {
    // Implement analytics integration here
    if (import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true') {
      logger.debug(
        '📊 Sending route metrics to analytics:',
        routePerformance,
        'performance'
      )
    }
  }

  /**
   * Check for performance alerts
   */
  private checkPerformanceAlerts(metric: PerformanceMetric): void {
    if (metric.rating === 'poor') {
      // Only log in development with performance logging enabled
      if (
        import.meta.env.DEV &&
        import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true'
      ) {
        console.warn(
          `⚠️ Performance Alert: ${metric.name} is poor (${metric.value})`
        )
      }

      // Show user-facing notification for critical metrics
      const criticalMetrics = ['LCP', 'CLS', 'INP']
      if (criticalMetrics.includes(metric.name)) {
        this.showPerformanceNotification(metric)
      }
    }
  }

  /**
   * Show performance notification to user
   */
  private showPerformanceNotification(metric: PerformanceMetric): void {
    // Only show in development to avoid bothering users
    if (process.env.NODE_ENV === 'development') {
      if (import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS === 'true') {
        logger.warn(
          `⚠️ Performance Alert: ${metric.name} is poor (${metric.value})`,
          null,
          'performance'
        )
      }
    }
  }

  /**
   * Calculate performance rating
   */
  private calculateRating(
    metricName: string,
    value: number
  ): 'good' | 'needs-improvement' | 'poor' {
    const threshold =
      PERFORMANCE_THRESHOLDS[metricName as keyof typeof PERFORMANCE_THRESHOLDS]
    if (!threshold) return 'good'

    if (value <= threshold.good) return 'good'
    if (value <= threshold.poor) return 'needs-improvement'
    return 'poor'
  }

  /**
   * Get navigation type
   */
  private getNavigationType(): string {
    if ('navigation' in performance && performance.navigation) {
      const types = ['navigate', 'reload', 'back_forward', 'prerender']
      return types[performance.navigation.type] || 'unknown'
    }
    return 'unknown'
  }

  /**
   * Collect device information
   */
  private collectDeviceInfo(): DeviceInfo {
    const deviceInfo: DeviceInfo = {
      userAgent: navigator.userAgent,
      screen: {
        width: screen.width,
        height: screen.height,
        pixelRatio: window.devicePixelRatio || 1,
      },
    }

    // Network information (if available)
    const nav = navigator as Navigator & {
      connection?: { effectiveType: string; downlink: number; rtt: number }
      mozConnection?: { effectiveType: string; downlink: number; rtt: number }
      webkitConnection?: {
        effectiveType: string
        downlink: number
        rtt: number
      }
    }
    const connection =
      nav.connection || nav.mozConnection || nav.webkitConnection
    if (connection) {
      deviceInfo.connection = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
      }
    }

    // Memory information (if available)
    if ('memory' in performance) {
      deviceInfo.memory = (
        performance as Performance & {
          memory: { usedJSHeapSize: number }
        }
      ).memory.usedJSHeapSize
    }

    // Hardware concurrency
    if ('hardwareConcurrency' in navigator) {
      deviceInfo.hardwareConcurrency = navigator.hardwareConcurrency
    }

    return deviceInfo
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Generate unique metric ID
   */
  private generateMetricId(): string {
    return `metric_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Get performance summary for current route
   */
  public getRouteSummary(route?: string): PerformanceMetric[] {
    const targetRoute = route || this.currentRoute
    return this.metrics.get(targetRoute) || []
  }

  /**
   * Get all stored performance data
   */
  public getAllMetrics(): RoutePerformance[] {
    try {
      const data = localStorage.getItem('mntnd_performance_metrics')
      return data ? JSON.parse(data) : []
    } catch (error) {
      console.warn('Could not retrieve performance metrics:', error)
      return []
    }
  }

  /**
   * Clear all stored metrics
   */
  public clearMetrics(): void {
    this.metrics.clear()
    localStorage.removeItem('mntnd_performance_metrics')
  }

  /**
   * Disable performance monitoring
   */
  public disable(): void {
    this.isEnabled = false
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }

  /**
   * Enable performance monitoring
   */
  public enable(): void {
    this.isEnabled = true
    this.initializeMonitoring()
  }
}

// Create global instance
export const performanceMonitor = new PerformanceMonitor()

// Export for manual initialization
export default PerformanceMonitor
